import { computed } from 'vue';
import GeoJSON from 'ol/format/GeoJSON';
import type { Geometry } from 'ol/geom';
import { useFilterStore } from './useFilterStore';
import { useMonitoredAreasStore } from './useMonitoredAreasStore';
import { resolveSatelliteName, SAT_METADATA } from './useAoiStore';

export interface CoverageBucket {
  /** Bucket start epoch ms (inclusive) */
  startMs: number;
  /** Bucket end epoch ms (exclusive) */
  endMs: number;
  /** Average spatial coverage percentage across visible AOIs (0–100) */
  pct: number;
  /** Per-AOI breakdown: fraction of each AOI area covered in this bucket */
  aoiPcts: number[];
  /** Pass IDs (aoi-SAT-start-end) of features overlapping this bucket */
  passIds: string[];
  /**
   * Coverage-weighted average cloud cover (0–100) over the portion of the AOI
   * imaged in this bucket. Null when no contributing pass carries cloud data.
   */
  cloudPct: number | null;
  /**
   * Per-sensor-type proportional breakdown of bucket.pct.
   * Sorted by descending pct. sum(pct) ≈ bucket.pct.
   */
  sensorBreakdown: { sensorType: string; pct: number }[];
}

const BUCKET_DURATION_MS = 60 * 60 * 1000; // 1 hour
const geoJsonFormat = new GeoJSON();

/** Number of sample points per axis inside the AOI bounding box */
const GRID_SIZE = 10;

/**
 * Build a set of sample points inside an AOI polygon for coverage estimation.
 * Returns the sample coordinates (EPSG:4326 [lon, lat]) that fall inside the AOI.
 */
function buildAoiSamplePoints(aoiGeojson: Record<string, unknown>): [number, number][] {
  try {
    const features = geoJsonFormat.readFeatures(aoiGeojson, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:4326',
    });
    if (!features.length) return [];

    // Use the first polygon-like geometry
    let aoiGeom: Geometry | null = null;
    for (const f of features) {
      const g = f.getGeometry();
      if (g) { aoiGeom = g; break; }
    }
    if (!aoiGeom) return [];

    const ext = aoiGeom.getExtent(); // [minLon, minLat, maxLon, maxLat]
    if (!Number.isFinite(ext[0])) return [];

    const dx = (ext[2] - ext[0]) / (GRID_SIZE + 1);
    const dy = (ext[3] - ext[1]) / (GRID_SIZE + 1);
    if (dx <= 0 || dy <= 0) return [];

    const points: [number, number][] = [];
    for (let ix = 1; ix <= GRID_SIZE; ix++) {
      for (let iy = 1; iy <= GRID_SIZE; iy++) {
        const lon = ext[0] + ix * dx;
        const lat = ext[1] + iy * dy;
        if (aoiGeom.intersectsCoordinate([lon, lat])) {
          points.push([lon, lat]);
        }
      }
    }
    return points;
  } catch {
    return [];
  }
}

/**
 * For a coverage feature geometry, return which sample point indices it covers.
 */
function computeCoveredPoints(
  geom: Record<string, unknown>,
  samplePoints: [number, number][],
): Set<number> {
  const covered = new Set<number>();
  try {
    const features = geoJsonFormat.readFeatures(
      { type: 'Feature', geometry: geom, properties: {} },
      { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:4326' },
    );
    for (const f of features) {
      const g = f.getGeometry();
      if (!g) continue;
      for (let i = 0; i < samplePoints.length; i++) {
        if (!covered.has(i) && g.intersectsCoordinate(samplePoints[i])) {
          covered.add(i);
        }
      }
    }
  } catch {
    // ignore parse errors
  }
  return covered;
}

/**
 * Build a pass ID that matches MapAoiLayer's buildCoveragePassId format.
 */
function buildPassId(satName: string, startStr: string, endStr: string): string {
  return `aoi-${satName}-${startStr}-${endStr}`;
}

/**
 * Extract the latitude extent [minLat, maxLat] from a GeoJSON geometry
 * by walking all coordinate arrays.
 */
export function getLatExtent(geojson: Record<string, unknown>): [number, number] | null {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let count = 0;

  const walk = (coords: unknown): void => {
    if (!Array.isArray(coords) || coords.length === 0) return;
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const lat = coords[1] as number;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      count++;
      return;
    }
    for (const child of coords) walk(child);
  };

  const processGeom = (g: Record<string, unknown>): void => {
    if (g.coordinates) walk(g.coordinates);
  };

  if (geojson.type === 'FeatureCollection' && Array.isArray(geojson.features)) {
    for (const feat of geojson.features as Record<string, unknown>[]) {
      const g = (feat as Record<string, unknown>).geometry as Record<string, unknown> | undefined;
      if (g) processGeom(g);
    }
  } else if (geojson.type === 'Feature') {
    const g = geojson.geometry as Record<string, unknown> | undefined;
    if (g) processGeom(g);
  } else if (geojson.coordinates) {
    processGeom(geojson);
  }

  return count > 0 && Number.isFinite(minLat) ? [minLat, maxLat] : null;
}

/**
 * Determine if a swath polygon represents a descending (N→S) pass by comparing
 * the average latitude near the ring start vs near the ring midpoint.
 * For a swath polygon, these correspond to opposite ends of the satellite ground track.
 * Returns true if descending (higher lat first), false if ascending.
 */
export function isDescendingPass(swathGeom: Record<string, unknown>): boolean {
  const coords = swathGeom.coordinates as unknown;
  if (!Array.isArray(coords)) return false;

  let ring: unknown[] | null = null;
  const type = swathGeom.type as string;
  if (type === 'Polygon' && Array.isArray(coords[0])) {
    ring = coords[0] as unknown[];
  } else if (type === 'MultiPolygon' && Array.isArray(coords[0]) && Array.isArray((coords[0] as unknown[])[0])) {
    ring = (coords[0] as unknown[])[0] as unknown[];
  }
  if (!ring || ring.length < 4) return false;

  // For a closed ring, exclude the closing duplicate vertex
  const first = ring[0] as number[];
  const last = ring[ring.length - 1] as number[];
  const n = (first[0] === last[0] && first[1] === last[1]) ? ring.length - 1 : ring.length;
  if (n < 4) return false;

  // Sample the average latitude near ring start vs ring midpoint.
  // Ring[0] is at one end of the swath; ring[n/2] is at the opposite end.
  const sampleCount = Math.max(1, Math.floor(n / 4));

  let startLatSum = 0;
  for (let i = 0; i < sampleCount; i++) startLatSum += (ring[i] as number[])[1];
  const startAvgLat = startLatSum / sampleCount;

  const midIdx = Math.floor(n / 2);
  const midSampleStart = Math.max(0, midIdx - Math.floor(sampleCount / 2));
  let midLatSum = 0;
  let midCount = 0;
  for (let i = midSampleStart; i < midSampleStart + sampleCount && i < n; i++) {
    midLatSum += (ring[i] as number[])[1];
    midCount++;
  }
  if (midCount === 0) return false;
  const midAvgLat = midLatSum / midCount;

  return startAvgLat > midAvgLat;
}

/** Typical LEO sun-synchronous ground track speed in km/s */
const LEO_GROUND_SPEED_KMS = 7.0;

/** 1 degree of latitude ≈ 111.32 km */
const KM_PER_DEGREE_LAT = 111.32;

/** Swath latitude span above which we assume GEO satellite (skip clipping) */
const GEO_LAT_THRESHOLD = 60;

/**
 * Clip global satellite pass times to the AOI crossing window.
 *
 * Uses AOI physical size + typical LEO ground speed to estimate crossing duration,
 * and latitude interpolation against the swath geometry to center the window
 * within the global pass. This approach works even when the coverage API
 * pre-clips swath geometry to the AOI region.
 *
 * Returns [clippedStartMs, clippedEndMs] or the original times if clipping isn't possible.
 */
export function clipTimesToAoi(
  globalStartMs: number,
  globalEndMs: number,
  swathGeom: Record<string, unknown> | undefined,
  aoiGeojson: Record<string, unknown>,
  precomputedAoiLatExt?: [number, number] | null,
  allowWideSwathClip = false,
): [number, number] {
  const aoiLatExt = precomputedAoiLatExt !== undefined ? precomputedAoiLatExt : getLatExtent(aoiGeojson);
  if (!aoiLatExt) return [globalStartMs, globalEndMs];

  const aoiLatSpanDeg = aoiLatExt[1] - aoiLatExt[0];
  if (aoiLatSpanDeg <= 0) return [globalStartMs, globalEndMs];

  const globalDuration = globalEndMs - globalStartMs;
  if (globalDuration <= 0) return [globalStartMs, globalEndMs];

  // Estimate how long the satellite spends crossing the AOI's latitude extent
  const aoiHeightKm = aoiLatSpanDeg * KM_PER_DEGREE_LAT;
  const crossingDurationMs = Math.max(1000, (aoiHeightKm / LEO_GROUND_SPEED_KMS) * 1000);

  // If global pass is already short enough, keep as-is
  if (globalDuration <= crossingDurationMs * 1.5) return [globalStartMs, globalEndMs];

  // Determine the center time of the AOI crossing within the global pass
  let centerFraction = 0.5;

  if (swathGeom) {
    const swathLatExt = getLatExtent(swathGeom);
    if (swathLatExt) {
      const swathLatSpan = swathLatExt[1] - swathLatExt[0];

      // Very wide latitude coverage usually indicates a GEO satellite. The
      // swath view can override this because its active catalog is LEO-only;
      // a complete LEO orbit leg is also wider than this threshold.
      if (swathLatSpan > GEO_LAT_THRESHOLD && !allowWideSwathClip) {
        return [globalStartMs, globalEndMs];
      }

      // Only use interpolation for centering if swath covers significantly more latitude than AOI
      if (swathLatSpan > aoiLatSpanDeg * 1.5) {
        const aoiMidLat = (aoiLatExt[0] + aoiLatExt[1]) / 2;
        const clampedMidLat = Math.max(swathLatExt[0], Math.min(swathLatExt[1], aoiMidLat));
        const midFrac = (clampedMidLat - swathLatExt[0]) / swathLatSpan;

        const descending = isDescendingPass(swathGeom);
        centerFraction = descending ? (1 - midFrac) : midFrac;
        centerFraction = Math.max(0, Math.min(1, centerFraction));
      }
      // else: swath was pre-clipped to near the AOI, keep center at midpoint
    }
  }

  const centerMs = globalStartMs + centerFraction * globalDuration;
  const halfDur = crossingDurationMs / 2;

  return [
    Math.max(globalStartMs, centerMs - halfDur),
    Math.min(globalEndMs, centerMs + halfDur),
  ];
}

interface PassInterval {
  startMs: number;
  endMs: number;
  /** Pass ID matching MapAoiLayer's format (aoi-SAT-start-end) */
  passId: string;
  /** Indices of sample points covered by this pass's swath geometry */
  coveredPointIndices: Set<number>;
  /** Cloud cover forecast for this pass (0–100), if available */
  cloudCoverPct?: number;
  /** Sensor type extracted from pass properties (e.g. "LWIR", "SAR", "Multispectral") */
  sensorType: string;
}

export function useAoiCoverageBuckets() {
  const { timeRange, selectedSatelliteNames, cloudCoverThreshold } = useFilterStore();
  const { visibleAreas } = useMonitoredAreasStore();

  const buckets = computed<CoverageBucket[]>(() => {
    const [rangeStart, rangeEnd] = timeRange.value;
    if (!rangeStart || !rangeEnd || rangeEnd <= rangeStart) return [];

    const visible = visibleAreas.value;
    if (visible.length === 0) return [];

    const selectedUpper = new Set(selectedSatelliteNames.value.map((n) => n.toUpperCase()));
    if (selectedUpper.size === 0) return [];

    // Pre-compute per-area: sample points and pass intervals with coverage data.
    const aoiSampleCounts: number[] = [];
    const areaIntervals: PassInterval[][] = [];

    for (const area of visible) {
      const samplePoints = buildAoiSamplePoints(area.aoi_geojson);
      aoiSampleCounts.push(samplePoints.length);

      // Pre-compute AOI lat extent once per area for clipTimesToAoi
      const aoiLatExt = getLatExtent(area.aoi_geojson);

      const intervals: PassInterval[] = [];

      if (area.coverage_geojson) {
        const doc = area.coverage_geojson as Record<string, unknown>;
        const features = (doc.type === 'FeatureCollection' && Array.isArray(doc.features))
          ? doc.features as Record<string, unknown>[]
          : [doc];

        for (const feat of features) {
          const props = ((feat as Record<string, unknown>).properties ?? feat) as Record<string, unknown>;
          const satName = resolveSatelliteName(props);
          if (!satName || !selectedUpper.has(satName.toUpperCase())) continue;

          const startStr = String(props.start_time ?? props.startTime ?? props.start ?? '');
          const endStr = String(props.end_time ?? props.endTime ?? props.end ?? '');
          if (!startStr || !endStr) continue;

          const startMs = new Date(startStr).getTime();
          const endMs = new Date(endStr).getTime();
          if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;

          if (endMs < rangeStart || startMs > rangeEnd) continue;

          const geom = (feat as Record<string, unknown>).geometry as Record<string, unknown> | undefined;
          const coveredPointIndices = (samplePoints.length > 0 && geom)
            ? computeCoveredPoints(geom, samplePoints)
            : new Set<number>();

          // Prediction-service features are already trimmed to actual contact with
          // the AOI; clipping them again would replace exact times with an estimate.
          // Only scheduled footprints (whole-window times) still need the heuristic.
          const [clippedStart, clippedEnd] = props.times_exact
            ? [startMs, endMs]
            : clipTimesToAoi(startMs, endMs, geom, area.aoi_geojson, aoiLatExt);

          const rawCloud = props.cloud_cover_pct ?? props.cloudCoverPct ?? props.cloud_cover;
          const cloudCoverPct = (rawCloud != null && Number.isFinite(Number(rawCloud)))
            ? Number(rawCloud)
            : undefined;

          // Filter out passes exceeding the cloud cover threshold
          if (cloudCoverThreshold.value < 100 && cloudCoverPct != null && cloudCoverPct > cloudCoverThreshold.value) {
            continue;
          }

          // Coverage API only provides satellite_name/norad_id — resolve sensor type from catalog
          const sensorType = SAT_METADATA[satName]?.sensor_type ?? 'Other';

          intervals.push({
            startMs: clippedStart,
            endMs: clippedEnd,
            passId: buildPassId(satName, startStr, endStr),
            coveredPointIndices,
            cloudCoverPct,
            sensorType,
          });
        }
      }

      areaIntervals.push(intervals);
    }

    // Snap bucket grid to whole clock-hour boundaries so bars align with the
    // X-axis tick marks (which are also snapped to whole hours).
    const gridStart = Math.floor(rangeStart / BUCKET_DURATION_MS) * BUCKET_DURATION_MS;
    const bucketCount = Math.ceil((rangeEnd - gridStart) / BUCKET_DURATION_MS);
    const result: CoverageBucket[] = [];

    for (let i = 0; i < bucketCount; i++) {
      const bStart = Math.max(rangeStart, gridStart + i * BUCKET_DURATION_MS);
      const bEnd = Math.min(gridStart + (i + 1) * BUCKET_DURATION_MS, rangeEnd);

      const aoiPcts: number[] = [];
      const passIdSet = new Set<string>();
      // Per-sensor: sum of (sensorCoveredPoints.size / totalPoints) across AOIs with sample pts
      const sensorFracSums = new Map<string, number>();
      let validAoiCount = 0;

      for (let a = 0; a < visible.length; a++) {
        const totalPoints = aoiSampleCounts[a];
        if (totalPoints <= 0) {
          // No sample points — fall back to binary (has any pass or not)
          const hasAnyPass = areaIntervals[a].some(
            (iv) => iv.startMs < bEnd && iv.endMs > bStart,
          );
          aoiPcts.push(hasAnyPass ? 100 : 0);
          for (const iv of areaIntervals[a]) {
            if (iv.startMs < bEnd && iv.endMs > bStart) passIdSet.add(iv.passId);
          }
          continue;
        }

        // Union all covered sample points, tracking per-sensor coverage
        const unionCovered = new Set<number>();
        const sensorCoveredPoints = new Map<string, Set<number>>();

        for (const iv of areaIntervals[a]) {
          if (iv.startMs < bEnd && iv.endMs > bStart) {
            passIdSet.add(iv.passId);
            let sc = sensorCoveredPoints.get(iv.sensorType);
            if (!sc) { sc = new Set<number>(); sensorCoveredPoints.set(iv.sensorType, sc); }
            for (const idx of iv.coveredPointIndices) {
              unionCovered.add(idx);
              sc.add(idx);
            }
          }
        }

        aoiPcts.push((unionCovered.size / totalPoints) * 100);

        for (const [sType, covSet] of sensorCoveredPoints) {
          sensorFracSums.set(sType, (sensorFracSums.get(sType) ?? 0) + covSet.size / totalPoints);
        }
        validAoiCount++;
      }

      // Average coverage across all visible AOIs
      const avgPct = aoiPcts.length > 0
        ? aoiPcts.reduce((sum, p) => sum + p, 0) / aoiPcts.length
        : 0;

      // Coverage-weighted average cloud cover across all passes in this bucket.
      // Each pass is weighted by how many sample points it covers so that passes
      // with a larger footprint influence the average more.
      let cloudWeightedSum = 0;
      let cloudWeightTotal = 0;
      for (let a = 0; a < visible.length; a++) {
        for (const iv of areaIntervals[a]) {
          if (iv.cloudCoverPct == null) continue;
          if (iv.startMs >= bEnd || iv.endMs <= bStart) continue;
          const weight = iv.coveredPointIndices.size > 0 ? iv.coveredPointIndices.size : 1;
          cloudWeightedSum += iv.cloudCoverPct * weight;
          cloudWeightTotal += weight;
        }
      }
      const cloudPct = cloudWeightTotal > 0 ? cloudWeightedSum / cloudWeightTotal : null;

      // Build sensor breakdown: normalize per-sensor fracs so they sum to avgPct
      const sensorBreakdown: { sensorType: string; pct: number }[] = [];
      if (validAoiCount > 0 && sensorFracSums.size > 0 && avgPct > 0) {
        let totalFracSum = 0;
        for (const v of sensorFracSums.values()) totalFracSum += v;
        if (totalFracSum > 0) {
          const sorted = [...sensorFracSums.entries()].sort((a, b) => b[1] - a[1]);
          for (const [sType, fracSum] of sorted) {
            const pct = (fracSum / totalFracSum) * avgPct;
            if (pct > 0.05) sensorBreakdown.push({ sensorType: sType, pct });
          }
        }
      }

      result.push({
        startMs: bStart,
        endMs: bEnd,
        pct: avgPct,
        aoiPcts,
        passIds: [...passIdSet],
        cloudPct,
        sensorBreakdown,
      });
    }

    return result;
  });

  return { coverageBuckets: buckets };
}
